import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import {
  obterRelatorioAdmin,
  RelatorioAdminResponse,
  formatarPorcentagem,
  obterCorTaxaConclusao,
  FiltrosRelatorio,
  obterMetricasColaborador,
  MetricasColaboradorResponse,
  obterMetricasDepartamento,
  MetricasDepartamentoResponse
} from '../../services/relatorioService';
import { exportarRelatorioAdmin, exportarMetricasColaborador, exportarMetricasDepartamento } from '../../services/exportacaoService';
import { useAuth } from '../../contexts/AuthContext';
import TempoMedioPorStatus from './TempoMedioPorStatus';
import TempoMedioPorDepartamento from './TempoMedioPorDepartamento';
import ProdutividadeDetalhada from './ProdutividadeDetalhada';
import TarefasMaisAntigas from './TarefasMaisAntigas';
import GargalosIdentificados from './GargalosIdentificados';
import FiltrosRelatorioComponent from './FiltrosRelatorio';
import SkeletonLoader from '../common/SkeletonLoader';
import MetricasColaborador from './MetricasColaborador';
import MetricasDepartamento from './MetricasDepartamento';

const RelatorioAdmin: React.FC = () => {
  const { usuario } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string>('');
  const [relatorio, setRelatorio] = useState<RelatorioAdminResponse | null>(null);
  const [relatorioGeral, setRelatorioGeral] = useState<RelatorioAdminResponse | null>(null);
  const [filtrosAtivos, setFiltrosAtivos] = useState<FiltrosRelatorio>({});
  const [metricasColaborador, setMetricasColaborador] = useState<MetricasColaboradorResponse | null>(null);
  const [carregandoColaborador, setCarregandoColaborador] = useState(false);
  const [metricasDepartamento, setMetricasDepartamento] = useState<MetricasDepartamentoResponse | null>(null);
  const [carregandoDepartamento, setCarregandoDepartamento] = useState(false);

  const carregarRelatorio = async (periodo?: { dataInicio: string; dataFim: string }, filtros?: FiltrosRelatorio) => {
    setCarregando(true);
    setErro('');

    try {
      const filtrosParaUsar = filtros || filtrosAtivos;
      
      // Usar datas dos filtros se disponíveis, senão usar período
      let dataInicio: string | undefined;
      let dataFim: string | undefined;

      if (filtrosParaUsar.dataInicio || filtrosParaUsar.dataFim) {
        dataInicio = filtrosParaUsar.dataInicio;
        dataFim = filtrosParaUsar.dataFim;
      } else if (periodo) {
        dataInicio = periodo.dataInicio;
        dataFim = periodo.dataFim;
      }

      const dados = await obterRelatorioAdmin(dataInicio, dataFim, filtrosParaUsar);
      setRelatorio(dados);
      
      // Se não há filtros ativos, também atualizar relatório geral
      if (!filtrosParaUsar || Object.keys(filtrosParaUsar).length === 0 || 
          (!filtrosParaUsar.departamentos && !filtrosParaUsar.status && !filtrosParaUsar.prioridades && !filtrosParaUsar.usuarios)) {
        setRelatorioGeral(dados);
      } else {
        // Carregar dados gerais em paralelo para exportação geral
        const dadosGerais = await obterRelatorioAdmin(dataInicio, dataFim, {});
        setRelatorioGeral(dadosGerais);
      }
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar relatório');
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setCarregando(false);
    }
  };

  const handleFiltrosChange = (novosFiltros: FiltrosRelatorio) => {
    setFiltrosAtivos(novosFiltros);
    carregarRelatorio(undefined, novosFiltros);
  };

  const carregarMetricasColaborador = useCallback(async (usuarioId: string, filtros?: FiltrosRelatorio) => {
    setCarregandoColaborador(true);
    try {
      const dados = await obterMetricasColaborador(usuarioId, filtros);
      setMetricasColaborador(dados);
    } catch (error: any) {
      console.error('[RelatorioAdmin] Erro ao carregar métricas do colaborador:', error);
      setMetricasColaborador(null);
    } finally {
      setCarregandoColaborador(false);
    }
  }, []);

  const carregarMetricasDepartamento = useCallback(async (departamentoId: string, filtros?: FiltrosRelatorio) => {
    setCarregandoDepartamento(true);
    console.log('[RelatorioAdmin] Carregando métricas do departamento:', departamentoId);
    try {
      const dados = await obterMetricasDepartamento(departamentoId, filtros);
      console.log('[RelatorioAdmin] Métricas do departamento carregadas:', dados);
      setMetricasDepartamento(dados);
    } catch (error: any) {
      console.error('[RelatorioAdmin] Erro ao carregar métricas do departamento:', error);
      console.error('[RelatorioAdmin] Detalhes do erro:', error.response?.data);
      setMetricasDepartamento(null);
    } finally {
      setCarregandoDepartamento(false);
    }
  }, []);

  const handleExportarFiltrado = () => {
    if (relatorio) {
      exportarRelatorioAdmin(relatorio, filtrosAtivos);
    }
  };

  const handleExportarGeral = () => {
    if (relatorioGeral) {
      exportarRelatorioAdmin(relatorioGeral);
    } else if (relatorio) {
      exportarRelatorioAdmin(relatorio);
    }
  };

  const handleExportarColaborador = () => {
    if (metricasColaborador) {
      exportarMetricasColaborador({
        colaborador: metricasColaborador.colaborador,
        metricas: metricasColaborador.metricas,
        periodo: metricasColaborador.periodo,
        filtros: filtrosAtivos
      });
    }
  };

  const handleExportarDepartamento = () => {
    if (metricasDepartamento) {
      exportarMetricasDepartamento({
        departamento: metricasDepartamento.departamento,
        metricas: metricasDepartamento.metricas,
        colaboradores: metricasDepartamento.colaboradores,
        periodo: metricasDepartamento.periodo,
        filtros: filtrosAtivos
      });
    }
  };

  useEffect(() => {
    carregarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const usuarioUnico = filtrosAtivos.usuarios?.length === 1 ? filtrosAtivos.usuarios[0] : null;
    const departamentoUnico = filtrosAtivos.departamentos?.length === 1 ? filtrosAtivos.departamentos[0] : null;
    
    console.log('[RelatorioAdmin] useEffect disparado');
    console.log('[RelatorioAdmin] Filtros ativos:', JSON.stringify(filtrosAtivos));
    console.log('[RelatorioAdmin] Usuario único:', usuarioUnico);
    console.log('[RelatorioAdmin] Departamento único:', departamentoUnico);
    
    // Prioridade: se tem 1 usuário selecionado, mostra métricas do colaborador
    if (usuarioUnico) {
      console.log('[RelatorioAdmin] Carregando métricas de colaborador');
      carregarMetricasColaborador(usuarioUnico, filtrosAtivos);
      setMetricasDepartamento(null);
    } 
    // Se não tem usuário mas tem 1 departamento, mostra métricas do departamento
    else if (departamentoUnico) {
      console.log('[RelatorioAdmin] Carregando métricas de departamento');
      carregarMetricasDepartamento(departamentoUnico, filtrosAtivos);
      setMetricasColaborador(null);
    } 
    // Se não tem nenhum dos dois, limpa ambos
    else {
      console.log('[RelatorioAdmin] Limpando métricas');
      setMetricasColaborador(null);
      setMetricasDepartamento(null);
    }
  }, [filtrosAtivos, carregarMetricasColaborador, carregarMetricasDepartamento]);

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>;
  }

  if (!relatorio) {
    return <Alert severity="info" sx={{ mt: 2 }}>Nenhum dado disponível</Alert>;
  }
  const { resumoGeral, porDepartamento, topUsuarios, tarefasAtrasadas, tendenciasMensais } = relatorio;

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AssessmentIcon sx={{ mr: 1 }} />
          Relatório Administrativo
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {relatorio.periodo.filtroData
            ? `${relatorio.periodo.dataInicio} até ${relatorio.periodo.dataFim}`
            : 'Todos os períodos'}
        </Typography>
      </Box>

      {/* Filtros Avançados */}
      <FiltrosRelatorioComponent
        onFiltrosChange={handleFiltrosChange}
        tipoRelatorio="admin"
        onExportar={handleExportarFiltrado}
        onExportarGeral={handleExportarGeral}
        mostrarBotaoExportar={usuario?.tipo === 'admin'}
        filtrosAplicados={filtrosAtivos}
      />

      {/* Métricas do Colaborador Individual (quando apenas 1 selecionado) */}
      {filtrosAtivos.usuarios?.length === 1 && (
        <>
          {carregandoColaborador && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" sx={{ mb: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {!carregandoColaborador && metricasColaborador && (
            <MetricasColaborador 
              colaborador={metricasColaborador.colaborador}
              metricas={metricasColaborador.metricas}
              onExportar={handleExportarColaborador}
            />
          )}
        </>
      )}

      {/* Métricas do Departamento (quando apenas 1 departamento selecionado e nenhum usuário) */}
      {filtrosAtivos.departamentos?.length === 1 && (!filtrosAtivos.usuarios || filtrosAtivos.usuarios.length === 0) && (
        <>
          {carregandoDepartamento && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" sx={{ mb: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {!carregandoDepartamento && metricasDepartamento && (
            <MetricasDepartamento 
              departamento={metricasDepartamento.departamento}
              metricas={metricasDepartamento.metricas}
              colaboradores={metricasDepartamento.colaboradores}
              onExportar={handleExportarDepartamento}
            />
          )}
        </>
      )}

      {/* Cards de Resumo Geral - sempre visível quando não há filtro específico */}
      {filtrosAtivos.usuarios?.length !== 1 && filtrosAtivos.departamentos?.length !== 1 && (
        <>
      {/* Cards de Resumo Geral */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="text.secondary">Usuários Ativos</Typography>
              </Box>
              <Typography variant="h4">{resumoGeral.totalUsuarios}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="text.secondary">Departamentos</Typography>
              </Box>
              <Typography variant="h4">{resumoGeral.totalDepartamentos}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="text.secondary">Total de Tarefas</Typography>
              </Box>
              <Typography variant="h4">{resumoGeral.totalTarefas}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Taxa de Conclusão</Typography>
              <Typography variant="h4" sx={{ color: obterCorTaxaConclusao(resumoGeral.taxaConclusaoGeral) }}>
                {formatarPorcentagem(resumoGeral.taxaConclusaoGeral)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {resumoGeral.tarefasConcluidas} de {resumoGeral.totalTarefas}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Status das Tarefas */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Concluídas</Typography>
              <Typography variant="h5" color="success.main">{resumoGeral.tarefasConcluidas}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Pendentes</Typography>
              <Typography variant="h5" color="warning.main">{resumoGeral.tarefasPendentes}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Atrasadas</Typography>
              <Typography variant="h5" color="error.main">{resumoGeral.tarefasAtrasadas}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Concluídas com Atraso</Typography>
              <Typography variant="h5" color="error.main">{resumoGeral.tarefasConcluidasComAtraso || 0}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Performance por Departamento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Performance por Departamento</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Departamento</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Concluídas</TableCell>
                  <TableCell align="right">Atrasadas</TableCell>
                  <TableCell align="right">Usuários Ativos</TableCell>
                  <TableCell align="right">Taxa de Conclusão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {porDepartamento.map((dept) => (
                  <TableRow key={dept.departamentoId}>
                    <TableCell>{dept.nome}</TableCell>
                    <TableCell align="right">{dept.totalTarefas}</TableCell>
                    <TableCell align="right">
                      <Chip label={dept.tarefasConcluidas} size="small" color="success" />
                    </TableCell>
                    <TableCell align="right">
                      {dept.tarefasAtrasadas > 0 ? (
                        <Chip label={dept.tarefasAtrasadas} size="small" color="error" />
                      ) : (
                        <Chip label="0" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">{dept.usuariosAtivos}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={dept.taxaConclusao}
                          sx={{
                            width: 100,
                            height: 6,
                            borderRadius: 3,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: obterCorTaxaConclusao(dept.taxaConclusao)
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40 }}>
                          {formatarPorcentagem(dept.taxaConclusao)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Top Usuários */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Top 10 Usuários Mais Produtivos</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Posição</TableCell>
                  <TableCell>Usuário</TableCell>
                  <TableCell>Departamento</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Concluídas</TableCell>
                  <TableCell align="right">Taxa de Conclusão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topUsuarios.map((usuario, index) => (
                  <TableRow key={usuario.usuarioId}>
                    <TableCell>
                      <Chip 
                        label={`#${index + 1}`} 
                        size="small" 
                        color={index === 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{usuario.nome}</TableCell>
                    <TableCell>{usuario.departamento}</TableCell>
                    <TableCell align="right">{usuario.totalTarefas}</TableCell>
                    <TableCell align="right">
                      <Chip label={usuario.tarefasConcluidas} size="small" color="success" />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={usuario.taxaConclusao}
                          sx={{
                            width: 100,
                            height: 6,
                            borderRadius: 3,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: obterCorTaxaConclusao(usuario.taxaConclusao)
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40 }}>
                          {formatarPorcentagem(usuario.taxaConclusao)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Tarefas Atrasadas por Departamento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Tarefas Atrasadas por Departamento</Typography>
          <Typography variant="h4" color="error.main" sx={{ mb: 2 }}>
            {tarefasAtrasadas.total} tarefas atrasadas
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {Object.entries(tarefasAtrasadas.porDepartamento).map(([dept, qtd]) => (
              <Box 
                key={dept} 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 1, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1,
                  flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 30%' }
                }}
              >
                <Typography>{dept}</Typography>
                <Chip label={qtd} color="error" size="small" />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Tendências Mensais */}
      {tendenciasMensais.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Tendências Mensais</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mês</TableCell>
                    <TableCell align="right">Criadas</TableCell>
                    <TableCell align="right">Concluídas</TableCell>
                    <TableCell align="right">Taxa de Conclusão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tendenciasMensais.map((item) => {
                    const taxa = item.criadas > 0 ? Math.round((item.concluidas / item.criadas) * 100) : 0;
                    return (
                      <TableRow key={item.mes}>
                        <TableCell>{item.mes}</TableCell>
                        <TableCell align="right">{item.criadas}</TableCell>
                        <TableCell align="right">
                          <Chip label={item.concluidas} size="small" color="success" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: obterCorTaxaConclusao(taxa) }}>
                            {formatarPorcentagem(taxa)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ============================================ */}
      {/* NOVAS MÉTRICAS - FASE 2 */}
      {/* ============================================ */}

      {/* Gargalos Identificados */}
      {relatorio.gargalosIdentificados && relatorio.gargalosIdentificados.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <GargalosIdentificados dados={relatorio.gargalosIdentificados} />
        </Box>
      )}

      {/* Tarefas Mais Antigas */}
      {relatorio.tarefasMaisAntigas && relatorio.tarefasMaisAntigas.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TarefasMaisAntigas dados={relatorio.tarefasMaisAntigas} />
        </Box>
      )}

      {/* Tempo Médio por Status */}
      {relatorio.tempoMedioPorStatus && relatorio.tempoMedioPorStatus.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TempoMedioPorStatus dados={relatorio.tempoMedioPorStatus} />
        </Box>
      )}

      {/* Tempo Médio por Departamento */}
      {relatorio.tempoMedioPorDepartamento && relatorio.tempoMedioPorDepartamento.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TempoMedioPorDepartamento dados={relatorio.tempoMedioPorDepartamento} />
        </Box>
      )}

      {/* Produtividade Detalhada */}
      {relatorio.produtividadeDetalhada && relatorio.produtividadeDetalhada.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ProdutividadeDetalhada 
            dados={relatorio.produtividadeDetalhada}
            titulo="Top 20 Usuários Mais Produtivos (Score Detalhado)"
          />
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default RelatorioAdmin;
